import { DateTime } from "luxon";

import { GooseGameQuestType } from "src/api/types.generated";

import * as Types from "../../api/types.generated";

export interface Quest {
  id: number;
  title: string;
  description?: string;
  points_rewarded: number;
  completion_limit: number;
  type: QuestType;
  meta?: Record<string, unknown> | null;
  quest_event_activity_category?: string; // for SCANNED
  question_string?: string; // for QUESTION
  question_answer?: string; // for QUESTION
  event_attendee_id?: number;
  created_at?: string;
  flash: boolean;
  hidden: boolean;
}

export enum QuestType {
  Scanned = "SCANNED",
  Question = "QUESTION",
  OrganizerApproved = "ORGANIZER_APPROVED",
  Networking = "NETWORKING",
  Badge = "BADGE",
}

export const questTypeMap: Record<QuestType, GooseGameQuestType> = {
  [QuestType.Scanned]: GooseGameQuestType.Scanned,
  [QuestType.Question]: GooseGameQuestType.Question,
  [QuestType.OrganizerApproved]: GooseGameQuestType.OrganizerApproved,
  [QuestType.Networking]: GooseGameQuestType.Networking,
  [QuestType.Badge]: GooseGameQuestType.Badge,
};

export interface QuestLog {
  id: number;
  event_attendee_id?: number;
  quest_id: number;
  created_at: number;
  scanned_event_attendee_id?: number; // for NETWORKING
  event_activity_id?: number; // for SCANNED
}

export interface StoreItem {
  id: number;
  event_id: number;
  name: string;
  description?: string;
  image_url?: string;
  point_cost: number;
  quantity_available: number;
  user_redemption_limit: number;
  created_at: string;
  is_sold_out: boolean;
}

export enum TransactionType {
  Quest = "QUEST",
  Purchase = "PURCHASE",
  ManualAdjustment = "MANUAL_ADJUSTMENT",
}

export interface Transaction {
  id: number;
  event_attendee_id: number;
  points_change: number;
  type: TransactionType;
  item_id?: number;
  quest_id?: number;
  created_at: string;
}

export const QuestPrimaryColorMap: Record<QuestType, string> = {
  [QuestType.Scanned]: "#9344ED",
  [QuestType.Question]: "#F99828",
  [QuestType.OrganizerApproved]: "#1AB3B0",
  [QuestType.Networking]: "#DA3EC3",
  [QuestType.Badge]: "#3E55DA",
};

export const QuestBackgroundColorMap: Record<QuestType, string> = {
  [QuestType.Scanned]: "#EADFF9",
  [QuestType.Question]: "#FFFBEB",
  [QuestType.OrganizerApproved]: "#DFF9F4",
  [QuestType.Networking]: "#FAE4FF",
  [QuestType.Badge]: "#E6EAFF",
};

export const QuestCardColors = {
  title: "#374151",
  flashTitle: "#D8B000",
  flashHighlight: "#FFE96B",
  flashBorderDark: "#D8B000",
  flashBorderLight: "#E4C440",
  flashBorderLighter: "#EBD267",
  flashBorderLightest: "#F3E49A",
  subtitle: "#6B7280",
  points: "#9CA3AF",
  border: "#D1D5DB",
  hover: "#F9FAFB",
} as const;

export const GooseToQuestTypeMap: Record<GooseGameQuestType, QuestType> = {
  [GooseGameQuestType.Scanned]: QuestType.Scanned,
  [GooseGameQuestType.Question]: QuestType.Question,
  [GooseGameQuestType.OrganizerApproved]: QuestType.OrganizerApproved,
  [GooseGameQuestType.Networking]: QuestType.Networking,
  [GooseGameQuestType.Badge]: QuestType.Badge,
};
export const QuestToGooseTypeMap: Record<QuestType, GooseGameQuestType> = {
  [QuestType.Scanned]: GooseGameQuestType.Scanned,
  [QuestType.Question]: GooseGameQuestType.Question,
  [QuestType.OrganizerApproved]: GooseGameQuestType.OrganizerApproved,
  [QuestType.Networking]: GooseGameQuestType.Networking,
  [QuestType.Badge]: GooseGameQuestType.Badge,
};

export type GGPlayer = {
  event_attendee?: {
    user: {
      id: number;
      name: string;
      email: string;
    };
    badge_code?: string | null;
  };
  event_attendee_id: number;
  current_points: number;
  total_points: number;
  created_at: string;
};

export interface GGPrize {
  id: number;
  name: string;
  description?: string | null;
  pointCost: number;
  quantityAvailable: number;
  redemptionLimit: number;
  isSoldOut: boolean;
}

export interface GGRedemptionHistory {
  name: string;
  pointCost: number;
  createdAt: DateTime;
  quantity: number;
}

export type TPrizeCheckout = GGPrize & { quantity: number; selected: boolean };

export enum Status {
  AVAILABLE = "Available",
  UNAVAILABLE = "SOLD OUT",
}

type GooseGameDashboardCatchUpQuestFields = Pick<
  Types.GooseGameQuest,
  | "id"
  | "title"
  | "type"
  | "points_rewarded"
  | "completion_limit"
  | "question_string"
  | "description"
  | "quest_event_activity_category"
>;

export type GooseGameDashboardCatchUpSummary = {
  completionCount: number;
  completionLimit: number;
  pointsEarned: number;
  occurredAt: string;
  quest: GooseGameDashboardCatchUpQuestFields;
};

export type GooseGameDashboardCatchUp = {
  totalPointsEarned: number;
  hasUpdates: boolean;
  progress: GooseGameDashboardCatchUpSummary[];
  completions: GooseGameDashboardCatchUpSummary[];
};
